// ─────────────────────────────────────────────────────────────────────────────
// USUARIOS + ASIGNACIÓN TÉCNICO ↔ USUARIO — INFRAESTRUCTURA COMPARTIDA con FluxAI.
//
// Espejo del contrato de FluxAI `web/src/lib/api.ts` (`listarUsuarios`, `asignarTecnico`,
// `desasignarTecnico`, `crearUsuarioBasico`, `actualizarUsuario`). Mismos NOMBRES, mismas
// FORMAS de dato → cuando se despliegue el `api/` real de FluxAI (autoridad de identidad de
// la suite Alebas) esta capa se sustituye por la Data API sin tocar la UI. Un usuario tiene
// UN técnico activo; reasignar reemplaza (regla de FluxAI `tecnico_usuarios`).
//
// Roles de plataforma (CHECK admin/usuario/tecnico, igual que FluxAI public.usuarios).
// Persistencia local de PRUEBAS (mientras no esté el `api/`): localStorage `sh:usuarios` y
// `sh:asignaciones`. Los clientes que cotizan (quotes) se siembran como usuarios 'usuario'.
// ─────────────────────────────────────────────────────────────────────────────

import { hubPost, hubConfigurado } from './hub';
import { tokenParaBackend } from './adminAuth';

const K_USERS = 'sh:usuarios';
const K_ASIGN = 'sh:asignaciones';

const norm = (e) => (e || '').toLowerCase().trim();
const ev = () => { try { window.dispatchEvent(new Event('solarhub-usuarios')); } catch { /* */ } };
// 🔑 Prefiere el token de ADMINISTRACIÓN (el único FIRMADO por el hub-api) y cae al de la suite.
// Desde que `exigeAdmin` falla cerrado (2026-08-29), el `alg:none` que acuña `mintToken` da 401 en
// las ops de administración: mandarlo sería dejar el panel del dueño muerto después de que se
// provisione el admin.
const sessionToken = () => tokenParaBackend();

function read(k) { try { return JSON.parse(localStorage.getItem(k) || '[]'); } catch { return []; } }
function write(k, v) { try { localStorage.setItem(k, JSON.stringify(v)); ev(); } catch { /* */ } }

// Email del técnico a partir de su id local/instalador (para el push email-based al backend).
function emailDeId(id) {
  const u = read(K_USERS).find((x) => x.id === Number(id));
  return u ? norm(u.email) : '';
}
function fireUsuarios(payload) {
  if (!hubConfigurado()) return;
  hubPost('usuarios', { ...payload, token: sessionToken() }).catch(() => { /* best-effort */ });
}

// Roles de Solar Hub. NO hay modelo de tenants ni "administrador" asignable acá: los roles
// INTERNOS de Alebas (dueño/soporte/admin) no se crean desde este panel. Los ASIGNABLES a
// usuarios de la plataforma son cliente final, técnico instalador y proveedor.
export const ROLES = [
  { id: 'usuario',   label: 'Usuario (cliente final)' },
  { id: 'tecnico',   label: 'Técnico instalador' },
  { id: 'proveedor', label: 'Proveedor' },
];
// Etiquetas de TODOS los roles posibles, incluidos los INTERNOS de Alebas que NO se asignan
// desde este panel (dueño/soporte/admin) — para MOSTRAR filas que ya vengan con esos roles. El
// 'support' (Soporte) es interno y compartido en toda la suite Alebas (mismo soporte para Solar
// Hub + FluxAI: support@fluxai.solutions). No se ofrece como opción asignable.
export const ROL_LABELS = {
  usuario: 'Usuario (cliente final)', tecnico: 'Técnico instalador', proveedor: 'Proveedor',
  admin: 'Administrador', support: 'Soporte', super_administrator: 'Dueño',
};
// Roles INTERNOS de Alebas (no asignables; existen en la identidad de la suite).
export const ROLES_INTERNOS = ['admin', 'support', 'super_administrator'];

// Correos INTERNOS de la suite (dueño/root + soporte): NUNCA son "cliente". Aunque hayan
// cotizado, no deben sembrarse ni listarse como usuario final (el dueño reportó "no soy
// usuario, soy root"). Espejo de auth.js (DEMO_ADMIN_EMAIL / SUPPORT_EMAIL).
const INTERNAL_EMAILS = new Set([
  (process.env.REACT_APP_ADMIN_EMAIL || 'ing@alebas.co').toLowerCase(),
  (process.env.REACT_APP_SUPPORT_EMAIL || 'support@fluxai.solutions').toLowerCase(),
]);
const OWNER_EMAIL = (process.env.REACT_APP_ADMIN_EMAIL || 'ing@alebas.co').toLowerCase();

let _seq = 1000;
function nextId(existing) {
  const max = existing.reduce((m, u) => Math.max(m, u.id || 0), _seq);
  return max + 1;
}

/** Usuario de plataforma (forma espejo de FluxAI `Usuario`). */
function readUsers() { return read(K_USERS); }

/**
 * Lista usuarios (opcionalmente filtrados por rol) — espejo de FluxAI `listarUsuarios(rol)`.
 * Siembra desde `quotes` los clientes que cotizaron y aún no son usuarios (rol 'usuario').
 */
export function listarUsuarios(rol, quotes = []) {
  const users = readUsers();
  const byEmail = new Map(users.map((u) => [norm(u.email), u]));
  // sembrar clientes de cotizaciones como usuarios 'usuario'
  let changed = false;
  for (const q of quotes) {
    const em = norm(q.email);
    if (!em || byEmail.has(em) || INTERNAL_EMAILS.has(em)) continue;   // root/soporte NO son cliente
    const u = { id: nextId(users), nombre: q.name || q.cliente || q.email, email: em, rol: 'usuario', telefono: q.phone || null, activo: true, creado_en: q.date || q.created_at || new Date().toISOString() };
    users.push(u); byEmail.set(em, u); changed = true;
  }
  if (changed) write(K_USERS, users);
  // Reclasifica correos internos persistidos por error como 'usuario' → su rol interno real,
  // para que NO aparezcan en las listas de "cliente" (técnico-asignación, etc.).
  const out = users.slice()
    .map((u) => INTERNAL_EMAILS.has(norm(u.email)) && (u.rol === 'usuario' || !u.rol)
      ? { ...u, rol: norm(u.email) === OWNER_EMAIL ? 'super_administrator' : 'support' } : u)
    .sort((a, b) => (a.nombre || '').localeCompare(b.nombre || ''));
  return rol ? out.filter((u) => u.rol === rol) : out;
}

/** Alta de usuario básico — espejo de FluxAI `crearUsuarioBasico`. */
export function crearUsuarioBasico({ nombre, email, rol = 'usuario', telefono = null }) {
  const users = readUsers();
  const em = norm(email);
  if (!em) throw new Error('Correo requerido.');
  if (users.some((u) => norm(u.email) === em)) throw new Error('Ya existe un usuario con ese correo.');
  const u = { id: nextId(users), nombre: nombre || em, email: em, rol, telefono, activo: true, creado_en: new Date().toISOString() };
  users.push(u); write(K_USERS, users);
  fireUsuarios({ op: 'create', email: em, nombre: u.nombre, rol, telefono: telefono || '' });
  return u;
}

/** Edita un usuario (nombre/rol/teléfono/activo) — espejo de FluxAI `actualizarUsuario`. */
export function actualizarUsuario(id, campos) {
  const users = readUsers();
  const i = users.findIndex((u) => u.id === id);
  if (i < 0) throw new Error('Usuario no encontrado.');
  users[i] = { ...users[i], ...campos };
  write(K_USERS, users);
  fireUsuarios({ op: 'update', email: norm(users[i].email), nombre: users[i].nombre || '', rol: campos.rol || '', telefono: campos.telefono || '', activo: campos.activo });
  return users[i];
}

/** Activa/desactiva un usuario. */
export function setActivoUsuario(id, activo) { return actualizarUsuario(id, { activo: !!activo }); }

// ── Asignación TÉCNICO ↔ USUARIO (FluxAI tecnico_usuarios / mis_usuarios) ──────

/** Lista de asignaciones activas (forma espejo de FluxAI `AsignacionTecnico`). */
export function listarAsignaciones() {
  return read(K_ASIGN).filter((a) => a.activo).sort((a, b) => (b.creado_en || '').localeCompare(a.creado_en || ''));
}

/** Asigna un técnico a un usuario (reemplaza el técnico anterior) — FluxAI `asignarTecnico`. */
export function asignarTecnico(clienteId, tecnicoId) {
  const cid = Number(clienteId), tid = Number(tecnicoId);
  if (!cid || !tid) throw new Error('Elige usuario y técnico.');
  const all = read(K_ASIGN).filter((a) => a.cliente_id !== cid); // un único técnico activo por usuario
  const row = { id: (all.reduce((m, a) => Math.max(m, a.id || 0), 0) || 0) + 1, cliente_id: cid, tecnico_id: tid, activo: true, creado_en: new Date().toISOString() };
  all.push(row); write(K_ASIGN, all);
  // Push email-based al backend (la suite identifica por email, no por id local).
  const cEmail = emailDeId(cid), tEmail = emailDeId(tid);
  const tNombre = readUsers().find((u) => u.id === tid)?.nombre || '';
  if (cEmail && tEmail) fireUsuarios({ op: 'assign', email: cEmail, tecnico_email: tEmail, tecnico_nombre: tNombre });
  return row;
}

/** Quita el técnico de un usuario — FluxAI `desasignarTecnico`. */
export function desasignarTecnico(clienteId) {
  const cid = Number(clienteId);
  const cEmail = emailDeId(cid);
  write(K_ASIGN, read(K_ASIGN).filter((a) => a.cliente_id !== cid));
  if (cEmail) fireUsuarios({ op: 'unassign', email: cEmail });
}

/**
 * Hidrata usuarios + asignaciones desde el backend (Postgres, por el hub-api) → cross-device.
 * Merge por email: el servidor es autoritativo de lo suyo; los usuarios locales que aún no
 * existen en el backend (sembrados de cotizaciones offline) se conservan. Mapea las
 * asignaciones email→id local para mantener estable la API id-based de la UI.
 */
/**
 * Trae el padrón del servidor y lo mezcla con el local.
 *
 * 🔴 DEVUELVE SI EL SERVIDOR CONTESTÓ, y por eso no es `void`: antes los tres caminos de fallo
 * —sin configurar, respuesta mala, excepción— salían igual que el éxito, así que el panel del
 * dueño mostraba «Aún no hay usuarios» con el padrón intacto del otro lado. Ese defecto ya pasó
 * acá con `list-quotes`: el admin creía estar viendo Postgres y veía su propio equipo.
 *
 * @returns {Promise<boolean>} `true` solo si el servidor entregó el padrón.
 */
export async function syncUsuarios() {
  if (!hubConfigurado()) return false;
  try {
    const r = await hubPost('usuarios', { op: 'list', token: sessionToken() });
    if (!r || !r.ok || !Array.isArray(r.usuarios)) return false;
    const localUsers = readUsers();
    const byEmail = new Map(localUsers.map((u) => [norm(u.email), u]));
    const mergedUsers = localUsers.slice();
    for (const sv of r.usuarios) {
      const em = norm(sv.email); if (!em) continue;
      const ex = byEmail.get(em);
      if (ex) { ex.nombre = sv.nombre || ex.nombre; ex.rol = sv.rol || ex.rol; ex.telefono = sv.telefono ?? ex.telefono; ex.activo = sv.activo !== false; }
      else { const u = { id: nextId(mergedUsers), nombre: sv.nombre || em, email: em, rol: sv.rol || 'usuario', telefono: sv.telefono || null, activo: sv.activo !== false, creado_en: sv.creado_en || new Date().toISOString() }; mergedUsers.push(u); byEmail.set(em, u); }
    }
    write(K_USERS, mergedUsers);
    // Reconstruir asignaciones (email→id) desde el servidor.
    const idDeEmail = (e) => byEmail.get(norm(e))?.id;
    const asign = [];
    let seq = 1;
    for (const sv of r.usuarios) {
      if (!sv.tecnico_email) continue;
      const cid = idDeEmail(sv.email), tid = idDeEmail(sv.tecnico_email);
      if (cid && tid) asign.push({ id: seq++, cliente_id: cid, tecnico_id: tid, activo: true, creado_en: new Date().toISOString() });
    }
    if (asign.length) write(K_ASIGN, asign);
    return true;
  } catch { return false; /* offline → se conserva la caché local, pero se DICE que no se pudo */ }
}

/** Técnico actualmente asignado a un usuario (id) o null. */
export function tecnicoDe(clienteId) {
  const a = listarAsignaciones().find((x) => x.cliente_id === Number(clienteId));
  return a ? a.tecnico_id : null;
}
